import { computed } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useBrand } from '~/composables/useBrand';
import { useProductType } from '~/composables/useProductType';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useExperiments from '~/stores/useExperimentsStore';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductDetails } from './useProductDetails';
import { PRODUCT_DETAILS_AVAILABILITY_REPOSITORY } from '../constants/providers';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';

export const useProductDetailsAvailability = () => injectLocal(PRODUCT_DETAILS_AVAILABILITY_REPOSITORY);

export const useProductDetailsAvailabilityProvide = () => {
  const { productData } = useProduct();
  const { isBcom, isMcom } = useBrand();
  const { isUnavailable, isConsolidatedPage } = useProductType();
  const { isSizeAndFitEnabledFlag, gwpExperienceEnabledFlag, isMemberOfMasterFlag, isProductHighlights } = useProductFlags();
  const { isBeautyIngredientsVisible, marketPlaceItemEnabled, isProductDimensionsVisible, isBigTicketV2C, isBigTicket, isShopTheSetExpEnabled } =
    useFeatureEligibility();
  const { dimensionsTitle, productSummaryBullets, sizeAndFit, dimensionsBullets, materialsAndCareBullets } = useProductDetails();
  const { memberFlags } = storeToRefs(useConsolidatedMemberStore());
  const {
    experimentFlags: { isProductHighlightsEnabled },
  } = useExperiments();

  const isSummaryAvailable = computed(() => !!productSummaryBullets.value.length);
  const isSizeAndFitAvailable = computed(() => {
    if (isBcom) {
      return ((isShopTheSetExpEnabled && memberFlags.value?.isSizeAndFitEnabledFlag) || isSizeAndFitEnabledFlag) && !!sizeAndFit.value?.length;
    }
    return !!sizeAndFit.value?.length;
  });

  const isIngredientsAvailable = computed(
    () => isBcom && !!dimensionsBullets.value?.length && !!dimensionsTitle.value && !isUnavailable && isBeautyIngredientsVisible,
  );

  const isMktDimensionsAvailable = computed(() => isBcom && marketPlaceItemEnabled && !isSizeAndFitAvailable.value);
  const isDimensionsEnabled = !isUnavailable && isProductDimensionsVisible;
  const isDimensionsAvailable = computed(
    () => !isIngredientsAvailable.value && !!dimensionsBullets.value?.length && (isDimensionsEnabled || isMktDimensionsAvailable.value),
  );
  const isMaterialAndCareAvailable = computed(() => !!materialsAndCareBullets.value?.length);
  const isInstructionsAvailable = isMcom && gwpExperienceEnabledFlag;
  const isTermsAndConditionsAvailable = isMcom && gwpExperienceEnabledFlag;
  const isWhiteGloveDeliveryAvailable = isBcom && !isBigTicketV2C && isBigTicket && !isMemberOfMasterFlag;
  const isShippingAndReturnsAvailable = !!Object.keys(productData?.shipping || {}).length && !isWhiteGloveDeliveryAvailable;
  const isHighlightsAvailable = computed(() => {
    if (!isProductHighlightsEnabled) {
      return false;
    }
    const isConsolidatedOrShopTheSet = isShopTheSetExpEnabled || isConsolidatedPage;
    return isConsolidatedOrShopTheSet ? !!memberFlags?.value?.isProductHighlights : !!isProductHighlights;
  });
  provideLocal(PRODUCT_DETAILS_AVAILABILITY_REPOSITORY, {
    // Computed
    isSummaryAvailable,
    isSizeAndFitAvailable,
    isIngredientsAvailable,
    isDimensionsAvailable,
    isMaterialAndCareAvailable,
    isHighlightsAvailable,
    // Common
    isInstructionsAvailable,
    isTermsAndConditionsAvailable,
    isWhiteGloveDeliveryAvailable,
    isShippingAndReturnsAvailable,
  });
};
